import React from 'react';
import { useStyletron } from 'baseui';
import { Card as BaseCard, StyledBody } from 'baseui/card';
import { Role } from '../chat-item/ChatItem';

import { CustomTheme } from '../baseweb-custom-theme/customTheme';

// Helper function to detect RTL text
const isRTLText = (text: string | React.ReactNode): boolean => {
  if (typeof text !== 'string') return false;
  // Unicode ranges for RTL scripts: Arabic, Hebrew, etc.
  const rtlRegex = /[\u0590-\u08FF\uFB1D-\uFDFD\uFE70-\uFEFC]/g;
  return rtlRegex.test(text);
};

export interface ChatBubbleProps {
  role?: Role;
  isTranslated?: boolean;
  isOrginalMessage?: boolean;
  text?: string;
  styleProp?: React.CSSProperties;
  children?: React.ReactNode;
  isRmTranslationsEnabled?: boolean;
}

export const ChatBubble = ({
  role = Role.Agent,
  isTranslated = false,
  isOrginalMessage = false,
  text,
  styleProp,
  children,
  isRmTranslationsEnabled = false,
}: ChatBubbleProps) => {
  const [css, theme] = useStyletron();
  const customTheme = theme as CustomTheme;
  // Detect if content is RTL
  const contentIsRTL = isRTLText(text || children);
  const direction = contentIsRTL ? 'rtl' : 'ltr';

  return (
    <BaseCard
      overrides={{
        Root: {
          style: ({ $theme }: { $theme: CustomTheme }) => ({
            display: 'flex',
            flexDirection: 'column',
            borderTopLeftRadius: $theme.borders.chatBubbleBorderRadius,
            borderTopRightRadius: $theme.borders.chatBubbleBorderRadius,
            borderBottomLeftRadius: $theme.borders.chatBubbleBorderRadius,
            borderBottomRightRadius: $theme.borders.chatBubbleBorderRadius,
            borderTopColor: $theme.borders.chatBubbleBorderColor,
            borderRightColor: $theme.borders.chatBubbleBorderColor,
            borderBottomColor: $theme.borders.chatBubbleBorderColor,
            borderLeftColor: $theme.borders.chatBubbleBorderColor,

            ...(role === Role.Agent
              ? {
                  backgroundColor: isTranslated
                    ? $theme.colors.chatBubbleBackgroundAgentTranslated
                    : $theme.colors.chatBubbleBackgroundAgent,
                }
              : {
                  backgroundColor: isTranslated
                    ? $theme.colors.chatBubbleBackgroundCustomerTranslated
                    : $theme.colors.chatBubbleBackgroundCustomer,
                }),
          }),
        },
        Contents: {
          style: ({ $theme }: { $theme: CustomTheme }) => ({
            marginTop: '0px',
            marginBottom: '0px',
            marginLeft: '0px',
            marginRight: '0px',
            paddingTop: $theme.sizing.chatBubblePadding,
            paddingBottom: $theme.sizing.twoWayChatFooterInputIconGap,
            paddingLeft: $theme.sizing.chatBubblePadding,
            paddingRight: $theme.sizing.chatBubblePadding,
            direction: 'inherit',
          }),
        },
        Body: {
          style: {
            marginBottom: '0px',
          },
        },
      }}
    >
      <StyledBody
        className={css({
          marginBottom: '0px',
          wordWrap: 'break-word',
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          direction: direction,
          textAlign: contentIsRTL ? 'right' : 'left',
          ...customTheme.typography.chatBubbleFont,
          ...(role === Role.Agent
            ? {
                color: customTheme.colors.chatBubbleTextAgent,
              }
            : {
                color: customTheme.colors.chatBubbleTextCustomer,
              }),
        })}
        dir={direction}
      >
        <div>{text || children}</div>
      </StyledBody>
      {isOrginalMessage && isRmTranslationsEnabled && (
        <div
          className={css({
            fontSize: '11px',
            marginTop: '4px',
            alignSelf: 'flex-end',
            textAlign: 'right',
            fontStyle: 'italic',
            color:
              role === Role.Agent
                ? customTheme.colors.chatBubbleTextAgent + '80'
                : customTheme.colors.chatBubbleTextCustomer + '80',
          })}
          dir="auto"
        >
          {role === Role.Agent ? 'sent message' : 'original message'}
        </div>
      )}
    </BaseCard>
  );
};
